import dayjs from 'dayjs';

import { FieldPath } from '../../types';
import { useValue } from './useValue';

const supportedOperations = ['multiply', 'add', 'add-months', 'add-days'];

const splitByOperation = (tokens: string[], ops = supportedOperations) => {
  const result = [];
  let current: string[] = [];

  for (const token of tokens) {
    // If the token is one of the operation keywords, start a new chunk
    if (ops.includes(token)) {
      // If we were already collecting a chunk, push it to the result first
      if (current.length) result.push(current);
      current = [token]; // new chunk starts with the op itself
    } else {
      current.push(token); // just keep adding operands
    }
  }

  // push the last chunk (if any)
  if (current.length) result.push(current);

  return result;
};

const runOperation = (operation: string, values: any[]) => {
  switch (operation) {
    case 'multiply': {
      return values.reduce((acc, num) => acc * Number(num ?? 1), 1);
    }
    case 'add': {
      return values.reduce((acc, num) => acc + Number(num ?? 0), 0);
    }
    case 'add-months': {
      return dayjs(values[0]).add(Number(values[1]), 'month').toDate();
    }
    case 'add-days': {
      return dayjs(values[0]).add(Number(values[1]), 'day').toDate();
    }
  }
};

export const useMathOperation = (expression?: string | FieldPath[]) => {
  const [operation, ...itemsToApplyOperation] = Array.isArray(expression)
    ? expression
    : [];

  const [values] = useValue(
    operation && itemsToApplyOperation?.length > 0
      ? { paths: itemsToApplyOperation }
      : { paths: [] }
  );

  // example smth like ['multiply', 'a', 'b', 'add', 'c'], as this should become
  // (a*b) + c
  const hasNestedOperations = values?.some((value) =>
    supportedOperations?.includes(value as (typeof supportedOperations)[number])
  );
  if (hasNestedOperations) {
    const allOperations = splitByOperation(values);
    allOperations[0].unshift(operation as string); // prepend the main operation, as we removed it on first step

    let result = null;
    for (let i = 0; i < allOperations.length; i++) {
      const [curOperation, ...curValues] = allOperations[i];
      if (i === 0) {
        result = runOperation(curOperation, curValues);
      } else if (result) {
        result = runOperation(curOperation, [result, ...curValues]);
      }
    }

    return result;
  }

  return runOperation(operation as string, values) ?? expression;
};
